import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import DefaultCard from 'components/card/DefaultCard'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sorting } from 'sorting'
import { HeaderCellProps } from 'tableTypes'
import { formatNumberToNumberWithSpecifiedDecimals } from 'utils/commonUtils'
import { MtcDriversTableRow } from 'mtcTypes'
import { MappedDriver } from 'reportViewer'
import DriversTableRow from './MolecularBiomarkersTableRow'
import HeaderCell from '../../../components/table/elements/HeaderCell'
import { getFormattedBiomarkerName } from '../../../utils/driverUtils'
import { getMaxGenomateScoreFromLeveledList } from '../../../utils/mtcUtils'

type BaseProps = {
    driverData: Array<MappedDriver>
    isReportPage?: boolean
    refKey?: string
}

type DriversCardSortingProps = Sorting<'variant' | 'gbs'>

const DriversCard: React.FC<BaseProps> = ({
    driverData,
    isReportPage,
    refKey
}) => {
    const { t } = useTranslation('report')
    const [maxGbs, setMaxGbs] = React.useState<number>(0)
    const [matTableRows, setMatTableRows] = React.useState<
        Array<MtcDriversTableRow>
    >([])
    const [openRowId, setOpenRowId] = useState<number>()
    const [sorting, setSorting] = useState<DriversCardSortingProps>({
        property: 'gbs',
        direction: 'DESC'
    })

    const headCells: Array<HeaderCellProps> = [
        { id: 'chevron', sortable: false },
        { id: 'variant', sortable: false },
        { id: 'driverClassification', sortable: false },
        { id: 'gbs', sortable: true }
    ]

    const sortRows = (
        dataset: Array<MtcDriversTableRow>,
        sortingProps: DriversCardSortingProps
    ): MtcDriversTableRow[] => {
        const isAscending = sorting.direction === 'ASC'
        let sorted
        if (sortingProps.property === 'variant') {
            sorted = [...dataset].sort((a, b) => {
                return isAscending
                    ? a.variant.trim().localeCompare(b.variant.trim())
                    : b.variant.trim().localeCompare(a.variant.trim())
            })
        } else if (sortingProps.property === 'gbs') {
            sorted = [...dataset].sort((a, b) => {
                return isAscending ? a.gbs - b.gbs : b.gbs - a.gbs
            })
        }
        return sorted || []
    }

    useEffect(() => {
        const sorted = sortRows(matTableRows, sorting)
        setMatTableRows(sorted)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorting])

    useEffect(() => {
        const mappedData = (driverData || [])
            .map((driver, idx) => ({
                id: idx,
                variant: getFormattedBiomarkerName(driver.item, t, {
                    ...driver.item,
                    testType: driver.testType,
                    testResult: driver.testResult
                }),
                driverClassification: driver.driverClassification,
                gbs: formatNumberToNumberWithSpecifiedDecimals(driver.level, 2),
                shouldNavigate: true
            }))
            .sort((a, b) => b.gbs - a.gbs)

        const sortedDataset = sortRows(mappedData, sorting)
        setMatTableRows(sortedDataset)
        setMaxGbs(
            getMaxGenomateScoreFromLeveledList(
                driverData.map((driver) => ({
                    level: formatNumberToNumberWithSpecifiedDecimals(
                        driver.level,
                        2
                    )
                }))
            )
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driverData, t])

    const handleRowCollapse = (rowId: number) => {
        setOpenRowId((prevState: number | undefined) => {
            if (prevState === rowId) return undefined
            return rowId
        })
    }

    const handleSorting = (column: string) => {
        let newDirection: string
        if (sorting.property === column) {
            if (sorting.direction === 'ASC') {
                newDirection = 'DESC'
            } else {
                newDirection = 'ASC'
            }
        } else {
            newDirection = 'DESC'
        }

        const newSorting = {
            property: column,
            direction: newDirection
        }

        setSorting(newSorting as DriversCardSortingProps)
    }

    return (
        <DefaultCard
            refKey={refKey}
        >
            {matTableRows.length > 0 ? (
                <Table>
                    <TableHead>
                        <TableRow>
                            {headCells.map((cellData) => (
                                <HeaderCell
                                    key={cellData.id}
                                    cell={cellData}
                                    sorting={sorting}
                                    handleSorting={handleSorting}
                                    translationKey="biomarkersAndInhibitors.tableHeader"
                                    translationNamespace="report"
                                />
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {matTableRows.map((row) => {
                            const isRowOpen = row.id === openRowId

                            return (
                                <React.Fragment key={row.id}>
                                    <DriversTableRow
                                        shouldNavigate={isReportPage}
                                        isRowOpen={isRowOpen}
                                        handleRowCollapse={handleRowCollapse}
                                        row={{
                                            ...row
                                        }}
                                        collapsable={false}
                                        maxGbs={maxGbs}
                                    />
                                </React.Fragment>
                            )
                        })}
                    </TableBody>
                </Table>
            ) : (
                <span className="defaultCard__emptyState">
                    {t('biomarkersAndInhibitors.noDriversAndBiomarkersFound')}
                </span>
            )}
        </DefaultCard>
    )
}

export default DriversCard
